import { Component, OnInit, Injector, HostListener } from '@angular/core';
import { FormControl } from '@angular/forms';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { ProcedureService } from 'app/core/http/services/procedure.service';
import { BaseComponent } from 'app/modules/shared/components/base.component';
import { telecom } from 'app/core/models/Organization';
import { Address } from 'cluster';
import { OrganizationLocationService } from 'app/core/http/services/organization-location.service';
import { HttpParams } from '@angular/common/http';
import { HealthcareserviceSpecialists } from 'app/core/interfaces/healthcareservice';
import { firstValueFrom } from 'rxjs';
import { PractitionerService } from 'app/core/http/services/practitioner.service';
import { Practitioner } from 'app/core/interfaces/practitioner';
import { PractitionerInfoDialogComponent } from 'app/modules/shared/components/practitioner-info-dialog/practitioner-info-dialog.component';
import { PractitionerroleService } from 'app/core/http/services/practitionerrole.service';
import { ScheduleSelectPatientComponent } from 'app/modules/shared/components/schedule-select-patient/schedule-select-patient.component';
import {
  addScheduleSearchSuggestion,
  friendlyProcedureName,
  groupScheduleSearchSuggestions,
  normalizeScheduleSearch,
  ScheduleSearchMode,
  ScheduleSearchSuggestion,
  ScheduleSearchSuggestionGroup,
} from 'app/modules/shared/models/schedule-search.models';
import { formatSchedulePrice, summarizeSchedulePayment } from 'app/modules/shared/models/schedule-payment-summary';

interface SpecialistCatalogGroup {
  key: string;
  practitionerId: string;
  name: string;
  procedures: HealthcareserviceSpecialists[];
  clinicCount: number;
}

@Component({
  selector: 'src-procedure-by-specialist',
  templateUrl: './procedure-by-specialist.component.html',
  styleUrls: ['./procedure-by-specialist.component.scss'],
})
export class ProcedureBySpecialistComponent extends BaseComponent implements OnInit {
  public title = 'Agendar';
  public searchControl = new FormControl('');
  public procedures: HealthcareserviceSpecialists[] = [];
  public address: Address[] = [];
  public locations: any[] = [];
  public secretaryLogged: any = {};
  public latLong: any[] = [];
  public nextLatLong: [number, number] = [0, 0];
  public currentPage: number = 0;
  public pageSize: number = 10;
  public practititoners: Practitioner[] = [];
  public healthcares: any[] = [];
  public practitionerRoles: any[] = [];
  public selectedDatetime: any = '';
  public isByOrder: boolean = false;
  public byOrderStart: string = '';
  distance: number = 20000; // valor padrão do raio de distância

  public autocompleteSuggestions: ScheduleSearchSuggestion[] = [];
  public showAutocompletePanel: boolean = false;
  public showAutocompleteHint: boolean = false;
  public activeSuggestionIndex: number = -1;
  public activeSearchMode: ScheduleSearchMode = 'specialist';
  public expandedPractitionerId: string | null = null;
  public expandedScheduleKey: string | null = null;

  public hasSearched = false;

  public get search(): string {
    return this.searchControl.value || '';
  }

  public get showMessage(): boolean {
    if (this.loading || this.latLong.length == 0 || (this.procedures.length == 0 && this.latLong.length != 0)) {
      return true;
    }
    return false;
  }

  public get message(): string {
    if (this.loading) {
      return 'Buscando Exames mais proximos...';
    }
    if (this.latLong.length == 0) {
      return 'Ative sua localização para buscar os exames mais proximos';
    }
    if (this.procedures.length == 0 && this.latLong.length != 0) {
      return 'Não foi retornado nenhum exame para o raio definido...';
    }
    return '';
  }

  navigateTo(route: string) {
    this.router.navigate([route]);
  }

  public get proceduresFilter(): any[] {
    if (!this.procedures || !Array.isArray(this.procedures)) {
      return [];
    }

    const searchTerm = normalizeScheduleSearch(this.search);

    return this.procedures.filter((item: any) => {
      const address = item.address ? item.address[0] : {};

      const specialties = this.getSpecialtys(item.practitioner_id)?.map((specialty: any) => specialty.value) ?? [];
      const searchableValues = [
        friendlyProcedureName(item.healthcareservice_name || item.name),
        item.practitioner_name,
        item.organization_name,
        address?.city,
        address?.state,
        ...specialties,
      ];
      return !searchTerm || searchableValues.some((value) => normalizeScheduleSearch(value).includes(searchTerm));
    });
  }

  public get specialistGroups(): SpecialistCatalogGroup[] {
    const groups = new Map<string, SpecialistCatalogGroup>();
    this.proceduresFilter.forEach((procedure: HealthcareserviceSpecialists) => {
      if (!procedure.practitioner_id) return;

      const group = groups.get(procedure.practitioner_id) ?? {
        key: procedure.practitioner_id,
        practitionerId: procedure.practitioner_id,
        name: procedure.practitioner_name,
        procedures: [],
        clinicCount: 0,
      };
      if (
        !group.procedures.some(
          (item) => item.healthcareservice_id === procedure.healthcareservice_id && this.getClinicKey(item) === this.getClinicKey(procedure),
        )
      ) {
        group.procedures.push(procedure);
      }
      groups.set(procedure.practitioner_id, group);
    });

    return Array.from(groups.values()).map((group) => ({
      ...group,
      clinicCount: new Set(group.procedures.map((procedure) => this.getClinicKey(procedure))).size,
    }));
  }

  public get paginatedProcedures(): any[] {
    const start = this.currentPage * this.pageSize;
    const end = start + this.pageSize;
    return this.specialistGroups.slice(start, end);
  }

  trackBySpecialistGroup(_index: number, group: SpecialistCatalogGroup): string {
    return group.key;
  }

  public get totalPages(): number {
    return Math.ceil(this.specialistGroups.length / this.pageSize);
  }

  public get autocompleteSuggestionGroups(): ScheduleSearchSuggestionGroup[] {
    return groupScheduleSearchSuggestions(this.autocompleteSuggestions);
  }

  public get visibleAutocompleteSuggestions(): ScheduleSearchSuggestion[] {
    return this.autocompleteSuggestionGroups.reduce((suggestions, group) => suggestions.concat(group.suggestions), [] as ScheduleSearchSuggestion[]);
  }

  public get activeSuggestionId(): string | null {
    return this.activeSuggestionIndex >= 0 ? `schedule-search-option-${this.activeSuggestionIndex}` : null;
  }

  constructor(
    private procedureService: ProcedureService,
    private locationService: OrganizationLocationService,
    private practitionerService: PractitionerService,
    private practitionerRoleService: PractitionerroleService,
    private injector: Injector,
  ) {
    super(injector);
  }

  ngOnInit(): void {
    const userLogged = this.getUserLogged();

    this.secretaryLogged = {
      secretary_id: userLogged.id,
      name: userLogged.name,
      email: userLogged?.email,
      birthDate: userLogged.birthDate,
    };

    this.activeSearchMode = 'specialist';
    this.searchControl.setValue(localStorage.getItem('searchControl') ?? '', { emitEvent: false });
    this.latLong.length === 0 ? this.getCurrentPosition() : this.getProceduresNearest();

    this.setPatientEncounterRaio();
    this.getLocations();
    this.getPractitioners();
    this.getHealthcares();
    this.getPractitionerSpecilaties();

    this.setupAutocomplete();
  }

  setupAutocomplete(): void {
    this.searchControl.valueChanges.pipe(debounceTime(200), distinctUntilChanged()).subscribe((searchTerm) => {
      this.updateAutocompleteSuggestions(searchTerm);
      this.currentPage = 0;
    });
  }

  updateAutocompleteSuggestions(searchTerm: string | null): void {
    const term = searchTerm ?? '';
    localStorage.setItem('searchControl', term.toLowerCase());
    const trimmedTerm = term.trim();
    this.activeSuggestionIndex = -1;

    if (trimmedTerm.length < 2) {
      this.autocompleteSuggestions = [];
      this.showAutocompletePanel = false;
      this.showAutocompleteHint = trimmedTerm.length > 0;
      return;
    }

    const suggestions: ScheduleSearchSuggestion[] = [];
    this.procedures.forEach((procedure) => {
      addScheduleSearchSuggestion(
        suggestions,
        {
          label: friendlyProcedureName(procedure.healthcareservice_name || procedure.name),
          category: 'Exames e procedimentos',
          mode: 'procedure',
        },
        trimmedTerm,
      );
      addScheduleSearchSuggestion(
        suggestions,
        {
          label: procedure.practitioner_name,
          category: 'Especialistas',
          mode: 'specialist',
        },
        trimmedTerm,
      );
      addScheduleSearchSuggestion(
        suggestions,
        {
          label: procedure.organization_name,
          category: 'Clínicas e locais',
          mode: 'clinic',
        },
        trimmedTerm,
      );

      const location = this.locations.find((item) => item.organization.organization_id === procedure.organization_id);
      if (location?.city) {
        addScheduleSearchSuggestion(
          suggestions,
          {
            label: location.city,
            category: 'Clínicas e locais',
            mode: 'clinic',
          },
          trimmedTerm,
        );
      }

      this.getSpecialtys(procedure.practitioner_id).forEach((specialty: any) =>
        addScheduleSearchSuggestion(
          suggestions,
          {
            label: specialty.value,
            category: 'Especialistas',
            mode: 'specialist',
          },
          trimmedTerm,
        ),
      );
    });

    this.autocompleteSuggestions = suggestions;
    this.showAutocompletePanel = true;
    this.showAutocompleteHint = false;
  }

  getSuggestionId(suggestion: ScheduleSearchSuggestion): string {
    return `schedule-search-option-${this.visibleAutocompleteSuggestions.indexOf(suggestion)}`;
  }

  isSuggestionActive(suggestion: ScheduleSearchSuggestion): boolean {
    return this.visibleAutocompleteSuggestions.indexOf(suggestion) === this.activeSuggestionIndex;
  }

  activateSuggestion(suggestion: ScheduleSearchSuggestion): void {
    this.activeSuggestionIndex = this.visibleAutocompleteSuggestions.indexOf(suggestion);
  }

  onInputKeydown(event: KeyboardEvent): void {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      if (this.search.trim().length >= 2 && !this.showAutocompletePanel) {
        this.updateAutocompleteSuggestions(this.search);
      }

      const suggestions = this.visibleAutocompleteSuggestions;
      if (suggestions.length === 0) return;

      event.preventDefault();
      this.showAutocompletePanel = true;
      const direction = event.key === 'ArrowDown' ? 1 : -1;
      this.activeSuggestionIndex =
        this.activeSuggestionIndex < 0
          ? direction > 0
            ? 0
            : suggestions.length - 1
          : (this.activeSuggestionIndex + direction + suggestions.length) % suggestions.length;
      return;
    }

    if (event.key === 'Enter' && this.showAutocompletePanel) {
      event.preventDefault();
      const suggestion = this.visibleAutocompleteSuggestions[this.activeSuggestionIndex];
      if (suggestion) {
        this.selectSuggestion(suggestion);
      } else {
        this.showAutocompletePanel = false;
      }
      return;
    }

    if (event.key === 'Escape' && this.showAutocompletePanel) {
      event.preventDefault();
      this.showAutocompletePanel = false;
      this.activeSuggestionIndex = -1;
    }
  }

  selectSuggestion(suggestion: ScheduleSearchSuggestion): void {
    localStorage.setItem('searchControl', suggestion.label.toLowerCase());
    this.searchControl.setValue(suggestion.label, { emitEvent: false });
    this.showAutocompletePanel = false;
    this.activeSuggestionIndex = -1;
    this.navigateToMode(suggestion.mode);
  }

  navigateToMode(mode: ScheduleSearchMode): void {
    localStorage.setItem('searchControl', this.search.toLowerCase());
    this.expandedPractitionerId = null;
    this.expandedScheduleKey = null;
    this.currentPage = 0;
    const routes: Record<ScheduleSearchMode, string> = {
      procedure: '/secretarias-new/agendar/exame',
      specialist: '/secretarias-new/agendar/especialistas/exame',
      clinic: '/secretarias-new/agendar/clinicas',
    };
    this.router.navigate([routes[mode]]);
  }

  togglePractitioner(practitionerId: string): void {
    this.expandedPractitionerId = this.expandedPractitionerId === practitionerId ? null : practitionerId;
    this.expandedScheduleKey = null;
  }

  getScheduleKey(procedure: HealthcareserviceSpecialists): string {
    return `${procedure.practitioner_id}:${this.getClinicKey(procedure)}:${procedure.healthcareservice_id}`;
  }

  toggleSchedule(procedure: HealthcareserviceSpecialists): void {
    const key = this.getScheduleKey(procedure);
    this.expandedScheduleKey = this.expandedScheduleKey === key ? null : key;
  }

  onInputFocus() {
    if (this.search.trim().length >= 2) {
      this.updateAutocompleteSuggestions(this.search);
    } else {
      this.showAutocompleteHint = this.search.trim().length > 0;
    }
  }

  onInputBlur() {
    setTimeout(() => {
      this.showAutocompletePanel = false;
      this.activeSuggestionIndex = -1;
    }, 150);
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    const inputElement = document.querySelector('input[matInput]');
    const panelElement = document.querySelector('.autocomplete-panel');

    if (inputElement && panelElement) {
      const clickedInsideInput = inputElement.contains(event.target as Node);
      const clickedInsidePanel = panelElement.contains(event.target as Node);

      if (!clickedInsideInput && !clickedInsidePanel) {
        this.showAutocompletePanel = false;
        this.activeSuggestionIndex = -1;
      }
    }
  }

  hasButton() {
    if (this.latLong.length == 0) {
      return 'col-xs-12 col-lg-8 margin';
    }
    return 'col-xs-12 ';
  }

  getPractitioners() {
    this.practitionerService.listNew().subscribe({
      next: (res: Practitioner[]) => {
        this.practititoners = res;
      },
      error: (err) => {
        console.log(err);
      },
    });
  }

  getPractitionerSpecilaties() {
    this.practitionerRoleService.list().subscribe({
      next: (res: any) => {
        this.practitionerRoles = res;
        if (this.search.trim().length >= 2) this.updateAutocompleteSuggestions(this.search);
      },
      error: (err) => {
        console.log(err);
      },
    });
  }

  getPractitionerRole(practitioner_id: string): string | null {
    const practitionerRole = this.practitionerRoles.find((role: any) => role.practitioner_id === practitioner_id);
    if (!practitionerRole || !Array.isArray(practitionerRole.specialty)) return null;
    return practitionerRole.specialty.map((s: any) => `${s.value} - CRM: ${s.crm}`).join(', ');
  }

  getPractitionerPhoto(specialist_id: string) {
    const specialist = this.practititoners.find((practitioner) => practitioner.practitioner_id === specialist_id);
    const url = specialist && specialist.photo ? specialist.photo : this.defaultPractitionerPhoto;
    return url;
  }

  public getCurrentPosition() {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position: any) => {
          this.latLong = [position.coords.latitude, position.coords.longitude];

          this.getProceduresNearest();
          this.getLocations();
        },
        (error: any) => {
          switch (error.code) {
            case error.PERMISSION_DENIED:
              console.error('User denied the request for Geolocation.');
              break;
            case error.POSITION_UNAVAILABLE:
              console.error('Location information is unavailable.');
              break;
            case error.TIMEOUT:
              console.error('The request to get user location timed out.');
              break;
            default:
              console.error('An unknown error occurred.');
              break;
          }
        },
      );
    } else {
      console.log('Geolocation is not supported by this browser.');
    }
  }

  async getHealthcareService(healthcareservice_id: string): Promise<any> {
    return firstValueFrom(this.procedureService.show(healthcareservice_id));
  }

  async openSelectPatientForm(procedure: any) {
    try {
      const healthcareServiceDetails = await this.getHealthcareService(procedure.healthcareservice_id);

      const practitioner = {
        name: procedure.practitioner_name,
        practitioner_id: procedure.practitioner_id,
      };

      const procedureAdapter = {
        ...healthcareServiceDetails,
        name: healthcareServiceDetails.name,
        healthcareservice_id: healthcareServiceDetails.healthcareservice_id,
        organization: {
          organization_id: healthcareServiceDetails.organization.organization_id,
          name: healthcareServiceDetails.organization.name,
        },
      };

      await this.getOrganizationLocation(procedure);

      const data = {
        title: 'Agendar consulta',
        param: {
          procedure: procedureAdapter,
          type: 'Exame',
          address: this.getAddress(procedure?.organization_id),
          secretary: this.secretaryLogged,
          latLong: this.nextLatLong,
          practitioner: practitioner,
          isByOrder: this.isByOrder,
          byOrderStart: this.byOrderStart,
          datetime: this.selectedDatetime,
        },
      };

      this.dialog.openComponentDialog(ScheduleSelectPatientComponent, data, {
        width: '760px',
        maxWidth: '92vw',
        maxHeight: '90vh',
      });
    } catch (error) {
      console.error('Erro ao buscar HealthcareService:', error);
    }
  }

  async openSelectPatientRegistrationForm(procedure: any) {
    try {
      const healthcareServiceDetails = await this.getHealthcareService(procedure.healthcareservice_id);

      const practitioner = {
        name: procedure.practitioner_name,
        practitioner_id: procedure.practitioner_id,
      };

      const procedureAdapter = {
        ...healthcareServiceDetails,
        name: procedure.healthcareservice_name,
        healthcareservice_id: procedure.healthcareservice_id,
        organization: {
          organization_id: procedure.organization_id,
          name: procedure.organization_name,
        },
      };

      await this.getOrganizationLocation(procedure);

      const data = {
        title: 'Agendar consulta',
        param: {
          procedure: procedureAdapter,
          type: 'Exame',
          address: this.getAddress(procedure?.organization_id),
          secretary: this.secretaryLogged,
          latLong: this.nextLatLong,
          practitioner: practitioner,
          datetime: this.selectedDatetime,
          actor: 'SECRETARY',
        },
      };

      this.dialog.openComponentDialog(ScheduleSelectPatientComponent, data, {
        width: '760px',
        maxWidth: '92vw',
        maxHeight: '90vh',
      });
    } catch (error) {
      console.error('Erro ao buscar HealthcareService:', error);
    }
  }

  openGoogleMapsLocation(organization_id: string) {
    const location = this.locations.find((location) => location.organization.organization_id == organization_id);
    if (!location) return;

    const address = `${location.street}, ${location.number}, ${location.city}-${location.state}`;
    const encodedAddress = encodeURIComponent(address);
    const googleMapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodedAddress}`;

    window.open(googleMapsUrl, '_blank');
  }

  openPractitionerInfos(practitioner_id: string) {
    const specialist = this.practititoners.find((practitioner) => practitioner.practitioner_id === practitioner_id);
    const practitionerRole = this.practitionerRoles.find((role: any) => role.practitioner_id === practitioner_id);

    if (specialist!.photo == '') {
      specialist!.photo = this.defaultPractitionerPhoto;
    }

    const data = {
      param: {
        specialist,
        practitionerRole,
      },
    };

    this.dialog.openComponentDialog(PractitionerInfoDialogComponent, data);
  }

  getOrganizationLocation(procedure: any): Promise<void> {
    return new Promise((resolve, reject) => {
      const organizationId = procedure.organization_id;

      this.locationService.showByOrganization(organizationId).subscribe({
        next: (resOrganizationLocation) => {
          this.nextLatLong = [resOrganizationLocation.position.latitude ?? 0, resOrganizationLocation.position.longitude ?? 0];
          resolve();
        },
        error: (err) => {
          console.log(err);
          reject(err);
        },
      });
    });
  }

  getProcedures() {
    this.procedureService.getProcedures().subscribe((result: any[]) => {
      this.procedures = result.map(this.transformToFormData);
    });
  }

  getProceduresNearest() {
    this.procedures = [];
    this.loading = true;
    this.hasSearched = false;

    let params = new HttpParams();

    const position = this.latLong.join(',');

    params = params.set('position', position);
    params = params.set('radiusInMeters', this.distance);

    this.procedureService.listNearestBySpecialists(params).subscribe({
      next: (healthcareservices: HealthcareserviceSpecialists[]) => {
        this.procedures = healthcareservices;
        this.currentPage = 0;

        this.hasSearched = true;
        this.loading = false;
        if (this.search.trim().length >= 2) this.updateAutocompleteSuggestions(this.search);
      },
      error: (error) => {
        this.hasSearched = true;
        this.loading = false;
        console.log(error);
      },
    });
  }

  getLocations() {
    this.locationService.list().subscribe({
      next: (resLocation: any) => {
        const res = resLocation;
        this.locations = res.map((location: any) => this.transformLocation(location));
        if (this.search.trim().length >= 2) this.updateAutocompleteSuggestions(this.search);
      },
      error: (err) => {
        console.log('erro', err);
      },
    });
  }

  getHealthcares() {
    this.procedureService.list().subscribe({
      next: (resHealthcares: any) => {
        this.healthcares = resHealthcares;
      },
      error: (err) => {
        console.log('erro', err);
      },
    });
  }

  getSpecialtys(practitioner_id: string): any[] {
    const practitionerRole = this.practitionerRoles.find((role: any) => role.practitioner_id === practitioner_id);
    if (!practitionerRole || !Array.isArray(practitionerRole.specialty)) return [];
    return practitionerRole.specialty;
  }

  getMainSpecialty(practitionerId: string): string {
    return this.getSpecialtys(practitionerId)[0]?.value ?? 'Especialista';
  }

  getClinicsForPractitioner(group: SpecialistCatalogGroup): string[] {
    return Array.from(new Set(group.procedures.map((procedure) => this.getClinicDisplayName(procedure)).filter(Boolean)));
  }

  getServiceDisplayName(procedure: HealthcareserviceSpecialists): string {
    return friendlyProcedureName(procedure.healthcareservice_name || procedure.name);
  }

  getClinicDisplayName(procedure: HealthcareserviceSpecialists): string {
    const address = Array.isArray(procedure.address) ? procedure.address[0] : procedure.address;
    const city = address?.city ? `${address.city}${address.state ? `, ${address.state}` : ''}` : '';
    return city ? `${procedure.organization_name} - ${city}` : procedure.organization_name;
  }

  getClinicKey(procedure: HealthcareserviceSpecialists): string {
    const address = Array.isArray(procedure.address) ? procedure.address[0] : procedure.address;
    const signature = [address?.street, address?.number, address?.city, address?.state]
      .map((value) => normalizeScheduleSearch(value))
      .filter(Boolean)
      .join('|');
    return signature ? `${procedure.organization_id}:${signature}` : procedure.organization_id;
  }

  getLocation(organization_id: string) {
    const location = this.locations.find((location) => location.organization.organization_id == organization_id);
    if (!location) return null;
    return `${location?.street}, ${location.number}, ${location.city}-${location.state}`;
  }

  getHealthcarePrice(healthcareservice_id: string) {
    const healthcare = this.healthcares.find((healthcares: any) => healthcares.healthcareservice_id == healthcareservice_id);
    if (!healthcare) return null;
    const amount = summarizeSchedulePayment([healthcare]).privatePrice;
    return amount === null ? null : formatSchedulePrice(amount);
  }

  getHealthcarePaymentSummary(healthcareservice_id: string): string {
    const healthcare = this.healthcares.find((item: any) => item.healthcareservice_id == healthcareservice_id);
    return summarizeSchedulePayment(healthcare ? [healthcare] : []).compactLabel;
  }

  getAddress(organization_id: string) {
    const location = this.locations.find((location) => location.organization.organization_id == organization_id);
    return location;
  }

  getHospitalName(procedure: HealthcareserviceSpecialists) {
    if (procedure) {
      return `<br>Clínica: ${procedure.organization_name}  `;
    }
    return '';
  }

  getPractititionerName(procedure: any) {
    if (procedure) {
      return `<br>${procedure.practitionername}  `;
    }
    return '';
  }

  transformToFormData(healthcareserviceData: any): any {
    return {
      healthcareservice_id: healthcareserviceData.healthcareservice_id,
      name: healthcareserviceData.name,
      description: healthcareserviceData.description,
      organization: {
        name: healthcareserviceData.providedBy.display,
        organization_id: healthcareserviceData.providedBy.code,
      },
      extraDetails: healthcareserviceData.extraDetails,
      type: healthcareserviceData?.type[0]?.coding[0]?.display,
      price_low: parseInt(healthcareserviceData?.valueRange?.low?.value),
      price_high: parseInt(healthcareserviceData?.valueRange?.high?.value),
      status: healthcareserviceData.status,
      specialty: healthcareserviceData.specialty[0].coding[0].display,
      comment: healthcareserviceData.comments,
      appointmentRequired: healthcareserviceData.appointmentRequired,
    };
  }

  transformLocation(location: any) {
    const address = location?.address[0];
    const speciality = location?.type[0]?.coding[0];

    const newAddress: any = {};

    newAddress.organization = {
      name: location?.name,
      organization_id: location.organization_id,
    };

    const cep = address.postalCode;

    newAddress.cep = cep;
    newAddress.country = address?.country;
    newAddress.state = address?.state;
    newAddress.city = address?.city;

    newAddress.district = address?.district;
    const extraInfos = address?.line;

    if (extraInfos) {
      newAddress.street = extraInfos[0] ?? '';
      newAddress.number = extraInfos[1] ?? '';
      newAddress.complement = extraInfos[2] ?? '';
    }

    const contactInfo = this.extractContactInfo(location?.telecom);

    if (contactInfo) {
      newAddress.phone = contactInfo.phone ?? '';
      newAddress.mobile = contactInfo.mobile ?? '';
      newAddress.mail = contactInfo.email ?? '';
    }

    if (speciality) {
      newAddress.speciality = speciality?.display;
    }
    return newAddress;
  }

  extractContactInfo(telecom: telecom[]) {
    let email: string | null = null;
    let phone: string | null = null;
    let mobile: string | null = null;

    telecom.forEach((telecom) => {
      const { value, use, system } = telecom;
      if (system === 'email') {
        email = value;
      } else if (system === 'phone') {
        phone = value;
      } else if (system === 'mobile') {
        mobile = value;
      }
    });
    return { email, phone, mobile };
  }

  setPatientEncounterRaio() {
    const distanceInKm = localStorage.getItem('distancePatient') ?? '20000';

    this.distance = Number(distanceInKm);
  }

  formatLabel(value: number): string {
    localStorage.setItem('distancePatient', value.toString());

    if (value >= 1000) {
      return Math.round(value / 1000) + 'km';
    }

    return `${value}`;
  }

  onSelectedDatetime(event: any, procedure: any) {
    this.selectedDatetime = event.datetime;
    this.isByOrder = event.isByOrder;
    this.byOrderStart = event.starthour;

    this.openSelectPatientForm(procedure);
  }
}
